import styles from './StepByStep.module.scss';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DataNodeModel, NodeKind } from '../../pages/editor/diagram/nodes/DataNodeModel';
import { ITopology, useDebugFirstMutation, useDebugNextMutation, useDebugStreamMutation, useDeleteStreamMutation } from '../../app/apiSlice';
import { DiagramNodeTag } from '../DiagramNodeTag/DiagramNodeTag';
import { getAllPathsFromNode } from './path.utils';
import { DebugOutput, DebugOutputApi } from './debug-output';
import { Checkbox } from 'primereact/checkbox';
import { useGraph } from '../../hooks/useGraph';
import { useAppDispatch } from '../../hooks/hooks';
import { setDebugMode } from '../../pages/editor/editorSlice';
import { Dialog } from 'primereact/dialog';
import { IconMessage } from '../IconMessage/IconMessage';
import { Button } from 'primereact/button';

export enum DebugSource {
  FLOW = 'flow',
  JSON = 'json',
  EDS = 'eds',
}

const enum DebugState {
  UNKNOWN,
  STOPPED,
  STARTING,
  STARTED,
  STOPPING
}

export interface StepByStepApi {
  load: (node: DataNodeModel|undefined) => void;
}

export interface StepByStepProps {
  topology: ITopology;
  onStop?: () => void;
}

export const StepByStep = forwardRef((props: StepByStepProps, ref) => {

  const ALLOWED_SOURCES = ['S3 EDS V2'];

  const dispatch = useAppDispatch();

  const { getNodesByKind } = useGraph();

  const eltDebugOutput = useRef<any>(null);
  const [debugFirst] = useDebugFirstMutation();
  const [debugNext] = useDebugNextMutation();
  const [stopStream] = useDeleteStreamMutation();
  const [debugStream] = useDebugStreamMutation();
  const [currentNode, setCurrentNode] = useState<DataNodeModel>();
  const [pathIndex, setPathIndex] = useState<number>(0);
  const [paths, setPaths] = useState<DataNodeModel[][]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [debugState, setDebugState] = useState<DebugState>(DebugState.UNKNOWN);
  const [error, setError] = useState(undefined);

  // ---------------------------------------------------------------------------------------------------------
  // PUBLIC METHODS
  // ---------------------------------------------------------------------------------------------------------

  useImperativeHandle(ref, (): StepByStepApi => ({

    /** Methode used to load the panel with given node */
    load(node: DataNodeModel|undefined) {
      let newPaths: DataNodeModel[][] = [];
      if (node) {
        newPaths = getAllPathsFromNode(node);
      } else {
        const targetNodes = getNodesByKind('sink');
        targetNodes.forEach((target) => {
          newPaths.push(...getAllPathsFromNode(target));
        })
      }
      setPaths(newPaths);
      setPathIndex(0);
      setCurrentNode(newPaths[0][0]);
      setDebugState(DebugState.STOPPED);
      setDialogVisible(true);
    },

  }));

  // ---------------------------------------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------------------------------------

  useEffect(() => {
    if (paths.length > 0 && paths[pathIndex].length > 1) {
      setCurrentNode(paths[pathIndex][0]);
    }
  }, [pathIndex]);

  // ---------------------------------------------------------------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------------------------------------------------------------

  const closeDialog = () => {
    setError(undefined);
    stopDebug();
    eltDebugOutput && (eltDebugOutput.current as DebugOutputApi).close();
    setDialogVisible(false);
  }

  const startDebug = () => {
    setError(undefined);
    setDebugState(DebugState.STARTING);
    debugStream({
      id: props.topology.id
    }).unwrap().then(() => {
      setDebugState(DebugState.STARTED);
      dispatch(setDebugMode(true));
      if (currentNode) {
        eltDebugOutput && (eltDebugOutput.current as DebugOutputApi).open(currentNode);
      }
    }).catch((error) => {
      console.error(error);
      setError(error);
      setDebugState(DebugState.STOPPED);
    });
  }

  const stopDebug = () => {
    setDebugState(DebugState.STOPPING);
    stopStream({
      name: props.topology.id,
      env: props.topology.environnement,
    }).unwrap().then(() => {
      setDebugState(DebugState.STOPPED);
      setCurrentNode(paths[pathIndex][0]);
      dispatch(setDebugMode(false));
      eltDebugOutput && (eltDebugOutput.current as DebugOutputApi).close();
    }).catch((error) => console.error(error));
  }

  const getCurrentPath = (): DataNodeModel[]|undefined => {
    return paths[pathIndex];
  }

  const getPreviousNodeInPath = (node: DataNodeModel|undefined): DataNodeModel|undefined => {
    const path = getCurrentPath();
    if (path && node) {
      const nodeIndex = path.indexOf(node);
      return path[nodeIndex - 1];
    }
    return undefined;
  }

  const isSourceNode = (node: DataNodeModel|undefined): boolean => {
    const path = getCurrentPath();
    if (path && node) {
      return path.indexOf(node) === 0;
    } else {
      return false;
    }
  }

  const runNode = (node: DataNodeModel) => {
    const newCurrentNode = (node === currentNode) ? getPreviousNodeInPath(node) : node;
    const prevNode = getPreviousNodeInPath(newCurrentNode);
    if (newCurrentNode && prevNode) {
      if (isSourceNode(prevNode)) {
        callFirst(newCurrentNode, prevNode);
      } else {
        callNext(newCurrentNode, prevNode);
      }
    }
  }

  const callFirst = (node: DataNodeModel, previousNode: DataNodeModel) => {
    debugFirst({
      streamId: props.topology.id,
      boxId: node.getID(),
      srcId: previousNode.getID()
    }).unwrap().then(() => {
      setCurrentNode(node);
      eltDebugOutput && (eltDebugOutput.current as DebugOutputApi).open(node);
    }).catch((error) => console.error(error));
  }

  const callNext = (node: DataNodeModel, previousNode: DataNodeModel) => {
    debugNext({
      streamId: props.topology.id,
      boxId: node.getID(),
      prevBoxId: previousNode.getID()
    }).unwrap().then(() => {
      setCurrentNode(node);
      eltDebugOutput && (eltDebugOutput.current as DebugOutputApi).open(node);
    }).catch((error) => console.error(error));
  }

  const selectNextPath = () => {
    if (pathIndex < paths.length - 1)
    setPathIndex(pathIndex + 1);
  }

  const selectPreviousPath = () => {
    if (pathIndex > 0) {
      setPathIndex(pathIndex - 1);
    }
  }

  // ---------------------------------------------------------------------------------------------------------
  // CLASS NAMES
  // ---------------------------------------------------------------------------------------------------------

  const CN_PrevPathButton = () => {
    const classes = [styles['path-selector-button']];
    if (pathIndex === 0) {
      classes.push(styles['disabled']);
    }
    return classes.join(' ');
  }

  const CN_NextPathButton = () => {
    const classes = [styles['path-selector-button']];
    if (pathIndex === paths.length - 1) {
      classes.push(styles['disabled']);
    }
    return classes.join(' ');
  }

  const ICON_StartButton = () => {
    if (debugState === DebugState.STARTING || debugState === DebugState.STOPPING) {
      return 'pi pi-spin pi-spinner';
    } else {
      return 'pi pi-play';
    }
  }

  const ICON_StopButton = () => {
    if (debugState === DebugState.STARTING || debugState === DebugState.STOPPING) {
      return 'pi pi-spin pi-spinner';
    } else {
      return 'pi pi-stop';
    }
  }

  // ---------------------------------------------------------------------------------------------------------
  // PRIVATE HTML
  // ---------------------------------------------------------------------------------------------------------

  const renderPath = () => {
    const nodes = paths[pathIndex];
    if (nodes && currentNode) {
      return (
        <div className={styles['path']}>
          {nodes.map((node, index) => {
            return (
              <div key={'node-row-'+index}>
                <div className={styles['path-node']}>
                  <Checkbox
                    checked={nodes.indexOf(node) <= nodes.indexOf(currentNode)}
                    disabled={nodes.indexOf(node) > nodes.indexOf(currentNode) + 1}
                    onClick={() => runNode(node)}
                    style={{visibility: index === 0 || node.kind === NodeKind.SINK ? 'hidden' : 'visible'}}
                  />
                  <DiagramNodeTag
                    node={node}
                    active={node === currentNode}
                    disabled={nodes.indexOf(node) > nodes.indexOf(currentNode)}
                  />
                </div>
                {index < nodes.length - 1 && <div className={styles['separator']}></div>}
              </div>
            );
          })}
        </div>
      );
    } else {
      return <div className={styles['path']}>Aucun noeud</div>
    }
  }

  const StartPanel = () => {
    const source = paths[pathIndex][0];
    console.log('source : ', source);
    const isSourceAllowed = ALLOWED_SOURCES.includes(source.technicalName);
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <IconMessage icon={isSourceAllowed ? `fas fa-comment-dots fa-flip-horizontal` : 'fas fa-warning'}>
          {isSourceAllowed &&
              <div style={{lineHeight: '1.2rem', maxWidth: '300px'}}>
                Selectionnez un parcours de flux<br/>puis cliquez sur <b>Démarrer</b>
              </div>
          }
          {!isSourceAllowed &&
              <div style={{lineHeight: '1.2rem', maxWidth: '300px'}}>
                Les sources de type <span className={styles['node-tech-name']}>"{source.technicalName}"</span> ne sont pas supportées.
                Seule la source <span className={styles['node-tech-name']}>"S3 V2"</span> permet l'utilisation du mode pas à pas actuellement. Sélectionnez un autre parcours de flux.
              </div>
          }
        </IconMessage>
        <Button
          className="mt-3"
          label="Démarrer"
          icon={ICON_StartButton()}
          iconPos='right'
          rounded
          onClick={startDebug}
          disabled={debugState !== DebugState.STOPPED || !isSourceAllowed}
        />
      </div>
    );
  }

  const PathSelector = () => {
    return (
      <>
        <div className={CN_PrevPathButton()} onClick={() => selectPreviousPath()}>
          <i className="pi pi-caret-left"/>
        </div>
        <span style={{userSelect: 'none'}}>Flux {pathIndex + 1} / {paths?.length}</span>
        <div className={CN_NextPathButton()} onClick={() => selectNextPath()}>
          <i className="pi pi-caret-right"/>
        </div>
      </>
    );
  }

  // ---------------------------------------------------------------------------------------------------------
  // MAIN RENDERER
  // ---------------------------------------------------------------------------------------------------------

  return (
    <Dialog
      visible={dialogVisible}
      header={'Mode Pas à Pas'}
      onHide={closeDialog}
      style={{ width: '70vw', height: '70vh' }}>
      <div className={styles['node-debug']}>
        <div className={styles['left-panel']}>
          <div>
            <h1>Flux de données</h1>
            {renderPath()}
          </div>
          <div className={styles['button-bar']}>
            {debugState === DebugState.STARTED && <Button label="Arrêter" rounded icon={ICON_StopButton()} onClick={stopDebug} disabled={debugState !== DebugState.STARTED}/>}
            {debugState === DebugState.STOPPED && <PathSelector/>}
          </div>
        </div>
        <div className={styles['right-panel']}>
          {error &&
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <IconMessage icon={'fas fa-times-circle'} title={`Erreur code ${(error as any).status}`}>
                {(error as any).data?.message}
              </IconMessage>
            </div>
          }
          {debugState !== DebugState.STARTED && !error && <StartPanel/>}
          <div style={{display: debugState === DebugState.STARTED ? 'block' : 'none', height: '100%'}}>
            <DebugOutput ref={eltDebugOutput} topology={props.topology}/>
          </div>
        </div>
      </div>
    </Dialog>
  );
});